import { useState, useCallback, useRef } from 'react';
import messageService, { SendMessageRequest, Message, MessageResponse } from '../services/messageService';
import { Chatbot } from '../services/chatbotService';

const ENABLE_STREAMING = process.env.REACT_APP_ENABLE_STREAMING === 'true';

export const useMessages = (preloadedMessages: Message[] = [], preloadedSessionId: string = '') => {
  const [messages, setMessages] = useState<Message[]>(preloadedMessages);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>(preloadedSessionId);
  const cancelRequestedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  };

  const sendMessage = useCallback(async (chatbot: Chatbot, message: string, currentSessionId?: string) => {
    cancelRequestedRef.current = false;
    setLoading(true);
    setError(null);

    const effectiveSessionId = currentSessionId || sessionId || generateUUID();
    if (!sessionId && !currentSessionId) {
      setSessionId(effectiveSessionId);
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    const requestData: SendMessageRequest = {
      chatbotId: chatbot.id,
      sessionId: effectiveSessionId,
      message: message,
      apiKey: chatbot.api_key || ''
    };

    // --- STREAMING PATH ---
    if (ENABLE_STREAMING) {
      return new Promise<MessageResponse>((resolve, reject) => {
        const botMsgId = (Date.now() + 1).toString();
        let accumulatedMessage = '';

        // Add placeholder bot message that will be updated as tokens arrive
        const botMessage: Message = {
          id: botMsgId,
          role: 'bot',
          content: '',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);

        const controller = messageService.sendMessageStream(requestData, {
          onToken: (token: string) => {
            if (cancelRequestedRef.current) return;
            accumulatedMessage += token;
            setMessages(prev =>
              prev.map(m => m.id === botMsgId ? { ...m, content: (m.content || '') + token } : m)
            );
          },
          onDone: (meta) => {
            setLoading(false);
            abortControllerRef.current = null;
            if (meta.finalMessage) {
              setMessages(prev =>
                prev.map(m => m.id === botMsgId ? { ...m, content: meta.finalMessage! } : m)
              );
            }
            resolve({
              success: true,
              data: {
                message: meta.finalMessage || accumulatedMessage,
                tokens: meta.tokens,
                generationTimeMs: meta.generationTimeMs,
                confidence: meta.confidence,
              },
              timestamp: new Date().toISOString(),
              statusCode: 200,
            });
          },
          onError: (errMsg: string) => {
            setLoading(false);
            abortControllerRef.current = null;
            if (!cancelRequestedRef.current) {
              setError(errMsg);
              reject(new Error(errMsg));
            }
          },
        });

        abortControllerRef.current = controller;
      });
    }

    // --- STANDARD PATH ---
    try {
      const response: MessageResponse = await messageService.sendMessage(requestData);

      if (cancelRequestedRef.current) {
        setLoading(false);
        return response;
      }

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'bot',
        content: response.data.message || '',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
      return response;
    } catch (err: any) {
      if (!cancelRequestedRef.current) {
        setError(err.message || 'Failed to send message');
        throw err;
      }
      return { success: false, data: { message: '' }, timestamp: '', statusCode: 0 };
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const stopGeneration = useCallback(() => {
    cancelRequestedRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setLoading(false);
  }, []);

  const clearMessages = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    cancelRequestedRef.current = false;
    setLoading(false);
    setError(null);
    setMessages([]);
    setSessionId('');
  }, []);

  const loadMessages = useCallback((messages: Message[], sessionId: string) => {
    setMessages(messages);
    setSessionId(sessionId);
  }, []);

  const removeLastMessage = useCallback(() => {
    setMessages(prev => (prev.length > 0 ? prev.slice(0, -1) : prev));
  }, []);

  const removeLastTurnForRetry = useCallback(() => {
    setMessages(prev => {
      if (prev.length === 0) return prev;

      const next = [...prev];

      // Retry should replace the latest user turn and any in-progress/failed bot output.
      if (next[next.length - 1]?.role === 'bot') {
        next.pop();
      }
      if (next[next.length - 1]?.role === 'user') {
        next.pop();
      }

      return next;
    });
  }, []);

  return {
    messages,
    loading,
    error,
    sessionId,
    sendMessage,
    stopGeneration,
    clearMessages,
    loadMessages,
    removeLastMessage,
    removeLastTurnForRetry
  };
};

export default useMessages;
